import { useCallback, useEffect, useRef, useState } from "react";
import {
  Circle,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Volume2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { voiceApi } from "@/features/agents/api";
import { env } from "@/lib/env";
import { cn } from "@/lib/utils";
import ToolCallBlock, { type ToolCallData } from "./ToolCallBlock";

type CallState = "idle" | "connecting" | "active" | "ending";
type PipelineState = "listening" | "thinking" | "speaking" | "";

type Entry = {
  role: "user" | "assistant";
  text: string;
  timestamp: string;
  latency_ms?: number;
  kind?: "tool";
  tool?: ToolCallData;
};

// A model sometimes echoes a tool's raw JSON result (e.g. a list of calendar
// slots) as its own message. That's noise in the chat window — detect a pure
// JSON dump so we can hide it. A normal sentence never JSON-parses, so real
// replies are untouched.
function looksLikeToolDump(text: string): boolean {
  const t = (text || "").trim();
  if (!t || (t[0] !== "[" && t[0] !== "{")) return false;
  try {
    const v = JSON.parse(t);
    return typeof v === "object" && v !== null;
  } catch {
    return false;
  }
}

const STATE_BG: Record<string, string> = {
  listening: "bg-emerald-500",
  thinking: "bg-amber-500",
  speaking: "bg-indigo-500",
};

function fmtDuration(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function apiHost() {
  // Resolve the WS host from the configured API base URL when possible.
  // Otherwise default to the page host (works with the Vite proxy in dev).
  try {
    const base = new URL(env.apiBaseUrl, window.location.origin);
    if (base.host && base.host !== window.location.host) return base.host;
  } catch {
    // ignore
  }
  return window.location.host;
}

export default function VoiceTestTab({
  agentId,
  agentName,
}: {
  agentId: string;
  agentName: string;
}) {
  const [callState, setCallState] = useState<CallState>("idle");
  const [pipelineState, setPipelineState] = useState<PipelineState>("");
  const [error, setError] = useState("");
  const [callId, setCallId] = useState("");
  const [transcript, setTranscript] = useState<Entry[]>([]);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);

  const mutedRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const procRef = useRef<AudioWorkletNode | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  const cleanup = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (procRef.current) {
      procRef.current.disconnect();
      procRef.current = null;
    }
    if (ctxRef.current && ctxRef.current.state !== "closed") {
      ctxRef.current.close();
      ctxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "end_call" }));
      }
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  function playAudio(arr: ArrayBuffer) {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      const url = URL.createObjectURL(new Blob([arr], { type: "audio/mpeg" }));
      const a = new Audio(url);
      a.onended = () => {
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audioRef.current = a;
      a.play().catch(() => {
        audioRef.current = null;
      });
    } catch {
      // ignore
    }
  }

  function clearAudio() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }

  // AudioWorklet processor: batches ~128ms of mono Float32 mic samples and
  // posts them to the main thread. Loaded from a Blob so it needs no separate
  // asset/build step. Replaces the deprecated ScriptProcessorNode, which in
  // current Chrome fires only once and then stalls (mic goes dead after the
  // first frame, so the agent never hears the caller).
  const WORKLET_SRC =
    "class PCMCapture extends AudioWorkletProcessor{" +
    "constructor(){super();this._buf=[];this._n=0;}" +
    "process(inputs){const ch=inputs[0]&&inputs[0][0];" +
    "if(ch){this._buf.push(ch.slice(0));this._n+=ch.length;" +
    "if(this._n>=2048){const out=new Float32Array(this._n);let o=0;" +
    "for(const b of this._buf){out.set(b,o);o+=b.length;}" +
    "this.port.postMessage(out,[out.buffer]);this._buf=[];this._n=0;}}" +
    "return true;}}" +
    "registerProcessor('pcm-capture',PCMCapture);";

  // Resample a native-rate Float32 mic frame down to 16 kHz Int16 PCM (what
  // the server expects). Linear interpolation is plenty for speech STT.
  function floatTo16kPCM(input: Float32Array, inRate: number): Int16Array {
    if (inRate === 16000) {
      const out = new Int16Array(input.length);
      for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      return out;
    }
    const ratio = inRate / 16000;
    const outLen = Math.max(1, Math.floor(input.length / ratio));
    const out = new Int16Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const idx = i * ratio;
      const i0 = Math.floor(idx);
      const i1 = Math.min(i0 + 1, input.length - 1);
      const sample = input[i0] + (input[i1] - input[i0]) * (idx - i0);
      const s = Math.max(-1, Math.min(1, sample));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }

  async function startAudioCapture() {
    if (!streamRef.current || !wsRef.current) return;
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    // Use the hardware-native rate. Forcing { sampleRate: 16000 } makes Chrome
    // deliver a single buffer from a mic source and then stall — the exact
    // "one frame then dead" symptom. We downsample to 16k ourselves instead.
    const ctx = new Ctx();
    ctxRef.current = ctx;
    try {
      await ctx.resume(); // defeat autoplay-policy suspension
      const blobUrl = URL.createObjectURL(
        new Blob([WORKLET_SRC], { type: "application/javascript" }),
      );
      await ctx.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);
    } catch {
      setError("Microphone capture failed to start in this browser.");
      return;
    }
    // The call may have ended while the worklet module was loading.
    if (ctx.state === "closed" || !streamRef.current || !wsRef.current) return;
    const inRate = ctx.sampleRate;
    const source = ctx.createMediaStreamSource(streamRef.current);
    const node = new AudioWorkletNode(ctx, "pcm-capture");
    procRef.current = node;
    let sent = 0;
    node.port.onmessage = (e) => {
      if (
        mutedRef.current ||
        !wsRef.current ||
        wsRef.current.readyState !== WebSocket.OPEN
      ) {
        return;
      }
      const pcm = floatTo16kPCM(e.data as Float32Array, inRate);
      wsRef.current.send(pcm.buffer);
      sent += 1;
      if (sent === 1 || sent % 50 === 0) {
        console.log(
          `[voice-test] sent ${sent} audio batches @${inRate}Hz, ctx=${ctx.state}`,
        );
      }
    };
    ctx.onstatechange = () =>
      console.log(`[voice-test] audioContext state -> ${ctx.state}`);
    source.connect(node);
    // The worklet writes no output, so connecting it to the destination is
    // silent (no mic-to-speaker feedback) but keeps the graph rendering so
    // process() keeps firing for the whole call.
    node.connect(ctx.destination);
    console.log(`[voice-test] capture started @${inRate}Hz, ctx=${ctx.state}`);
  }

  function handleWsMessage(data: string) {
    try {
      const msg = JSON.parse(data);
      switch (msg.type) {
        case "call_started": {
          setCallState("active");
          const start = Date.now();
          tickRef.current = setInterval(() => {
            setDuration(Math.floor((Date.now() - start) / 1000));
          }, 1000);
          void startAudioCapture();
          break;
        }
        case "transcript":
          if (msg.user_message) {
            setTranscript((p) => [
              ...p,
              {
                role: "user",
                text: msg.user_message,
                timestamp: new Date().toISOString(),
              },
            ]);
          }
          if (msg.assistant_response && !looksLikeToolDump(msg.assistant_response)) {
            setTranscript((p) => [
              ...p,
              {
                role: "assistant",
                text: msg.assistant_response,
                timestamp: new Date().toISOString(),
                latency_ms: msg.latency_ms,
              },
            ]);
          }
          break;
        case "function_call":
          // Tool call announced — show it immediately with its input; the
          // output is filled in when the matching function_result arrives.
          setTranscript((p) => [
            ...p,
            {
              role: "assistant",
              kind: "tool",
              text: `🔧 ${msg.name}`,
              timestamp: new Date().toISOString(),
              tool: { name: msg.name, arguments: msg.args, pending: true },
            },
          ]);
          break;
        case "function_result":
          // Fill the most recent still-pending call of this tool with its
          // output, duration and any error. Calls run sequentially, so the
          // newest pending entry is the right one.
          setTranscript((p) => {
            const next = [...p];
            for (let i = next.length - 1; i >= 0; i--) {
              const e = next[i];
              if (e.kind === "tool" && e.tool?.pending && e.tool.name === msg.name) {
                next[i] = {
                  ...e,
                  tool: {
                    name: msg.name,
                    arguments: msg.args,
                    result: msg.output,
                    duration_ms: msg.duration_ms,
                    error: msg.error,
                    pending: false,
                  },
                };
                break;
              }
            }
            return next;
          });
          break;
        case "state":
          setPipelineState((msg.status as PipelineState) || "");
          break;
        case "call_ended":
          cleanup();
          setCallState("idle");
          setPipelineState("");
          break;
        case "flow_ended":
          setTranscript((p) => [
            ...p,
            {
              role: "assistant",
              text: `[Flow completed — outcome: ${msg.outcome || "success"}]`,
              timestamp: new Date().toISOString(),
            },
          ]);
          break;
        case "error":
          setError(msg.message || "Unknown error");
          break;
        case "interrupted":
          clearAudio();
          break;
      }
    } catch {
      // non-JSON, ignore
    }
  }

  async function startCall() {
    setError("");
    setTranscript([]);
    setDuration(0);
    setCallState("connecting");
    try {
      const init = await voiceApi.initiateCall(agentId);
      setCallId(init.call_id);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const tenantId = localStorage.getItem("tenantId") || "";
      const sep = init.ws_path.includes("?") ? "&" : "?";
      const wsUrl = `${proto}//${apiHost()}${init.ws_path}${sep}tenant_id=${tenantId}`;
      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: "start_call",
            agent_id: agentId,
            call_id: init.call_id,
          }),
        );
      };
      ws.onmessage = (ev) => {
        if (ev.data instanceof ArrayBuffer) playAudio(ev.data);
        else handleWsMessage(ev.data as string);
      };
      ws.onerror = () => {
        setError(
          "WebSocket connection failed. Check that the server supports WebSockets.",
        );
        setCallState("idle");
        cleanup();
      };
      ws.onclose = () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        if (procRef.current) {
          procRef.current.disconnect();
          procRef.current = null;
        }
        if (ctxRef.current && ctxRef.current.state !== "closed") {
          ctxRef.current.close();
          ctxRef.current = null;
        }
        wsRef.current = null;
        setCallState("idle");
        setPipelineState("");
      };
    } catch (err) {
      const e = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      setError(
        e?.response?.data?.error || e?.message || "Failed to start call",
      );
      setCallState("idle");
      cleanup();
    }
  }

  function endCall() {
    setCallState("ending");
    cleanup();
    setCallState("idle");
    setPipelineState("");
  }

  function toggleMute() {
    const next = !muted;
    setMuted(next);
    mutedRef.current = next;
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = !next;
      });
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 gap-3">
          <div>
            <CardTitle className="text-base">Voice Test Call</CardTitle>
            <CardDescription>
              Test {agentName} with a live voice conversation from your browser.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {callState === "active" && (
              <>
                <span className="inline-flex items-center gap-1 rounded-full border border-rose-300 px-2 py-0.5 text-xs text-rose-600 dark:border-rose-900 dark:text-rose-400">
                  <Circle className="size-2 fill-current" />
                  {fmtDuration(duration)}
                </span>
                {pipelineState && (
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize text-white",
                      STATE_BG[pipelineState] ?? "bg-slate-500",
                    )}
                  >
                    {pipelineState}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleMute}
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted ? (
                    <MicOff className="size-4 text-rose-500" />
                  ) : (
                    <Mic className="size-4 text-emerald-500" />
                  )}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={endCall}
                >
                  <PhoneOff className="size-4" />
                  End Call
                </Button>
              </>
            )}
            {callState === "idle" && (
              <Button
                size="sm"
                onClick={startCall}
                className="bg-emerald-600 text-white hover:bg-emerald-600/90"
              >
                <Phone className="size-4" />
                Start Call
              </Button>
            )}
            {callState === "connecting" && (
              <Button variant="outline" size="sm" disabled>
                Connecting…
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {error && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError("")}
            className="text-xs underline-offset-2 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <Card>
        <CardHeader className="border-b py-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            Conversation Transcript
            {transcript.length > 0 && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                {transcript.length} messages
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="h-[400px] overflow-y-auto px-4 py-3">
            {transcript.length === 0 && callState === "idle" && (
              <div className="flex h-full flex-col items-center justify-center gap-1 text-center text-sm text-muted-foreground">
                <Volume2 className="size-8 opacity-30" />
                <p>
                  Click "Start Call" to begin a voice conversation with your
                  agent.
                </p>
                <p className="text-xs">
                  Make sure your microphone is available and your browser
                  allows access.
                </p>
              </div>
            )}

            {transcript.length === 0 && callState === "active" && (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Call active — start speaking…
              </div>
            )}

            {transcript.map((entry, i) => {
              if (entry.kind === "tool") {
                return entry.tool ? (
                  <div key={i} className="mb-3 px-2">
                    <ToolCallBlock call={entry.tool} />
                  </div>
                ) : (
                  <div key={i} className="mb-3 flex justify-center">
                    <span className="rounded-full bg-muted px-2.5 py-0.5 text-[11px] text-muted-foreground">
                      {entry.text}
                    </span>
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  className={cn(
                    "mb-3 flex flex-col gap-0.5",
                    entry.role === "user" ? "items-end" : "items-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                      entry.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted",
                    )}
                  >
                    <p className="whitespace-pre-wrap">{entry.text}</p>
                  </div>
                  <div className="flex gap-2 px-1 text-[11px] text-muted-foreground">
                    <span>{entry.role === "user" ? "You" : agentName}</span>
                    {entry.latency_ms != null && (
                      <span>{entry.latency_ms}ms</span>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        </CardContent>
      </Card>

      {callId && callState === "idle" && transcript.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Call ID: {callId} · Duration: {fmtDuration(duration)} ·{" "}
          {transcript.length} messages
        </p>
      )}
    </div>
  );
}
